import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { clearCached } from '../lib/pageCache';
import secureStorage from '../lib/secureStorage';

const AuthContext = createContext(null);

// Default analysis settings (used when user has no profile or is not logged in)
const DEFAULT_ANALYSIS_SETTINGS = {
  dampening_factors: {
    adoption_speed: 0.3,       // Risk likelihood: lower = less risk
    leadership_alignment: 0.2, // Risk likelihood: lower = less risk
    operational_readiness: 0.25, // Risk likelihood: lower = less risk
  },
  current_maturity: 50,
  headcount: 1000,
  revenue_per_employee: 150000,
  intervention_cost: 100000,
  elasticity_scenario: 'moderate',
  industry_type: 'technology',
  company_size: 'large',
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [rbac, setRbac] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Forced password rotation flag — set when admin-created accounts
  // log in for the first time (or any account whose admin reset their
  // password). ProtectedRoute reads this and redirects to
  // /change-password until cleared by a successful POST
  // /api/auth/change-password.
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Impersonation state — Super Admin viewing as another user
  const [impersonating, setImpersonating] = useState(() => {
    try {
      const saved = sessionStorage.getItem('impersonating');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  // "View As Org" state for Super Admin org preview
  const [viewAsOrg, setViewAsOrgState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('viewAsOrg');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const setViewAsOrg = useCallback((orgId, orgName) => {
    const val = { id: orgId, name: orgName };
    setViewAsOrgState(val);
    sessionStorage.setItem('viewAsOrg', JSON.stringify(val));
  }, []);

  const clearViewAsOrg = useCallback(() => {
    setViewAsOrgState(null);
    sessionStorage.removeItem('viewAsOrg');
    // Also clear the companion module-preview role so exiting
    // the org preview also exits the role preview cleanly.
    sessionStorage.removeItem('viewAsRole');
  }, []);

  const startImpersonation = useCallback((targetUser) => {
    const val = {
      user_id: targetUser.user_id,
      email: targetUser.email,
      name: targetUser.name || targetUser.email,
      role: targetUser.roles?.[0]?.role || 'user',
      organization_id: targetUser.organization_id || null,
      organization_name: targetUser.organization_name || null,
    };
    setImpersonating(val);
    sessionStorage.setItem('impersonating', JSON.stringify(val));
    if (targetUser.organization_id) {
      setViewAsOrg(targetUser.organization_id, targetUser.organization_name || targetUser.organization_id);
    }
  }, [setViewAsOrg]);

  const stopImpersonation = useCallback(() => {
    setImpersonating(null);
    sessionStorage.removeItem('impersonating');
    clearViewAsOrg();
  }, [clearViewAsOrg]);

  const checkAuth = useCallback(async () => {
    // Skip the /auth/me probe entirely when there's no stored session
    // token — otherwise a logged-out load fires an expected-401 that
    // the browser logs as a console network error. No token → treat as
    // logged out without a request. After login the token exists, so
    // session restoration still runs normally.
    if (!secureStorage.getItem('session_token')) {
      setUser(null);
      setRbac(null);
      setLoading(false);
      return null;
    }
    try {
      const response = await api.get('/auth/me');
      secureStorage.setItem('cache_scope', response.data.user_id);
      setUser(response.data);
      setRbac(response.data.rbac || null);
      setMustChangePassword(!!response.data.must_change_password);
      // Auto-set viewAsOrg to the user's home org so org-scoped users
      // see their own data by default. Skip this for super admins —
      // they should default to a global ("all orgs") view and only
      // enter preview mode explicitly via the org switcher.
      const isSuper = !!response.data.rbac?.is_super_admin;
      const savedOrg = sessionStorage.getItem('viewAsOrg');
      if (!savedOrg && !isSuper && response.data.rbac?.organization_id) {
        const orgId = response.data.rbac.organization_id;
        const orgName = response.data.rbac.organization_name || orgId;
        setViewAsOrgState({ id: orgId, name: orgName });
        sessionStorage.setItem('viewAsOrg', JSON.stringify({ id: orgId, name: orgName }));
      }
      return response.data;
    } catch (err) {
      setUser(null);
      setRbac(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Organisation home timezone (drives "Data as of", notification and story timestamps).
  const [orgTz, setOrgTz] = useState(null);
  const effectiveOrgIdForTz = viewAsOrg?.id || user?.organization_id || user?.rbac?.organization_id || null;
  useEffect(() => {
    if (!user) { setOrgTz(null); return; }
    const q = effectiveOrgIdForTz ? `?organization_id=${encodeURIComponent(effectiveOrgIdForTz)}` : '';
    api.get(`/auth/org-timezone${q}`).then((r) => setOrgTz(r.data?.timezone || null)).catch(() => setOrgTz(null));
  }, [user, effectiveOrgIdForTz]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback (LoginPage) will exchange the session_id and establish the session first.
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {

      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, remember = true) => {
    setError(null);
    try {
      const response = await api.post('/auth/login', { email, password });
      // Store token FIRST so subsequent requests are authenticated.
      secureStorage.setPersist(remember);
      if (response.data.session_token) {
        secureStorage.setItem('session_token', response.data.session_token);
      }
      secureStorage.setItem('cache_scope', response.data.user?.user_id || '');
      setUser(response.data.user);
      setLoading(false);
      // Surface the forced-rotation flag from the login response so
      // ProtectedRoute can redirect immediately, even before /auth/me
      // resolves.
      setMustChangePassword(!!response.data.must_change_password);
      // Enrich with RBAC; failure must NOT invalidate the fresh session.
      try {
        const meResp = await api.get('/auth/me');
        setUser(meResp.data);
        setRbac(meResp.data.rbac || null);
        setMustChangePassword(!!meResp.data.must_change_password);
        const isSuper = !!meResp.data.rbac?.is_super_admin;
        const savedOrg = sessionStorage.getItem('viewAsOrg');
        if (!savedOrg && !isSuper && meResp.data.rbac?.organization_id) {
          const orgId = meResp.data.rbac.organization_id;
          const orgName = meResp.data.rbac.organization_name || orgId;
          setViewAsOrgState({ id: orgId, name: orgName });
          sessionStorage.setItem('viewAsOrg', JSON.stringify({ id: orgId, name: orgName }));
        }
      } catch (_meErr) { /* session still valid, keep user logged in */ }
      return response.data.user;
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async () => {
    // Self-service registration was removed — only Super Admins and
    // Organization Admins can create accounts (Admin Dashboard →
    // Create User → POST /api/rbac/users/create). The /register
    // route now redirects to /login. This shim stays so legacy
    // callers fail loudly instead of silently 404-ing.
    const message = (
      'Self-service signup is disabled. Ask your Super Admin or '
      + 'Organization Admin to provision your account.'
    );
    setError(message);
    throw new Error(message);
  };

  const loginWithGoogle = async (sessionId) => {
    setError(null);
    try {
      const response = await api.post('/auth/google/session', { session_id: sessionId });
      // Store token FIRST — this ensures all subsequent API calls are authenticated
      secureStorage.setPersist(true);
      if (response.data.session_token) {
        secureStorage.setItem('session_token', response.data.session_token);
      }
      secureStorage.setItem('cache_scope', response.data.user?.user_id || '');
      // CRITICAL: set user from the google/session response IMMEDIATELY so
      // `isAuthenticated` flips true even if the RBAC enrichment call below
      // fails. Previously we called `checkAuth()` here which wipes `user`
      // to null on ANY /auth/me error (transient network, race, cold-start),
      // causing ProtectedRoute to kick the just-signed-in user back to /login.
      setUser(response.data.user);
      setLoading(false);

      // Enrich with RBAC context via /auth/me. Failure here must NOT
      // invalidate the freshly-created session — the session_token is valid
      // and the /auth/google/session call already returned 200.
      try {
        const meResp = await api.get('/auth/me');
        setUser(meResp.data);
        setRbac(meResp.data.rbac || null);
        const isSuper = !!meResp.data.rbac?.is_super_admin;
        const savedOrg = sessionStorage.getItem('viewAsOrg');
        if (!savedOrg && !isSuper && meResp.data.rbac?.organization_id) {
          const orgId = meResp.data.rbac.organization_id;
          const orgName = meResp.data.rbac.organization_name || orgId;
          setViewAsOrgState({ id: orgId, name: orgName });
          sessionStorage.setItem('viewAsOrg', JSON.stringify({ id: orgId, name: orgName }));
        }
      } catch (_meErr) {
        // RBAC fetch failed post-login; session is still valid. The next
        // route mount / checkAuth call will fill rbac. Keep user logged in.
      }

      return response.data.user;
    } catch (err) {
      const message = err.response?.data?.detail || 'Google login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      // Ignore errors on logout
    }
    clearCached();
    setUser(null);
    setRbac(null);
    setImpersonating(null);
    setViewAsOrgState(null);
    sessionStorage.removeItem('impersonating');
    sessionStorage.removeItem('viewAsOrg');
    secureStorage.removeItem('session_token');
    secureStorage.removeItem('cache_scope');
  };

  const setPassword = async (newPassword) => {
    setError(null);
    try {
      await api.post('/auth/set-password', { new_password: newPassword });
      await checkAuth();
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map((e) => e?.msg || JSON.stringify(e)).join('; ') : (typeof detail === 'string' ? detail : 'Could not set password');
      setError(message);
      throw new Error(message);
    }
  };

  const updateProfile = async (updates) => {
    setError(null);
    try {
      const response = await api.put('/auth/profile', updates);
      setUser(response.data);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Profile update failed';
      setError(message);
      throw new Error(message);
    }
  };

  // Compute analysis defaults from user profile
  const profileDefaults = useMemo(() => {
    if (!user?.profile) return DEFAULT_ANALYSIS_SETTINGS;
    
    const profile = user.profile;
    return {
      dampening_factors: profile.dampening_factors || DEFAULT_ANALYSIS_SETTINGS.dampening_factors,
      current_maturity: profile.current_maturity ?? DEFAULT_ANALYSIS_SETTINGS.current_maturity,
      headcount: profile.total_workforce || DEFAULT_ANALYSIS_SETTINGS.headcount,
      revenue_per_employee: profile.revenue_per_employee || DEFAULT_ANALYSIS_SETTINGS.revenue_per_employee,
      intervention_cost: profile.default_intervention_budget || DEFAULT_ANALYSIS_SETTINGS.intervention_cost,
      elasticity_scenario: profile.preferred_elasticity_scenario || DEFAULT_ANALYSIS_SETTINGS.elasticity_scenario,
      industry_type: profile.industry_type || DEFAULT_ANALYSIS_SETTINGS.industry_type,
      company_size: profile.company_size || DEFAULT_ANALYSIS_SETTINGS.company_size,
    };
  }, [user?.profile]);

  // Check if the user has customized their profile settings
  const hasProfileDefaults = useMemo(() => {
    return !!user?.profile;
  }, [user?.profile]);

  const changePassword = async (currentPassword, newPassword) => {
    setError(null);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setMustChangePassword(false);
      return true;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = (
        Array.isArray(detail)
          ? detail.map((e) => e?.msg || JSON.stringify(e)).join('; ')
          : (typeof detail === 'string' ? detail : 'Password change failed')
      );
      setError(message);
      throw new Error(message);
    }
  };

  const value = {
    user,
    rbac,
    orgTz,
    loading,
    error,
    isAuthenticated: !!user,
    isSuperAdmin: rbac?.is_super_admin || false,
    isOrgAdmin: rbac?.primary_role === 'org_admin' || rbac?.is_super_admin || false,
    userRole: rbac?.primary_role || 'user',
    needsOnboarding: rbac?.needs_onboarding || false,
    mustChangePassword,
    impersonating,
    startImpersonation,
    stopImpersonation,
    viewAsOrg,
    setViewAsOrg,
    clearViewAsOrg,
    login,
    register,
    loginWithGoogle,
    logout,
    updateProfile,
    changePassword,
    setPassword,
    checkAuth,
    profileDefaults,
    hasProfileDefaults,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
